import { SmsMessageType } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { normalizeSmsText, SMS_MAX_CHARS } from '../helpers/gsm7.js';
import { normalizePhoneNumber } from '../helpers/normalize-phone-number.js';
import { renderSms } from './sms-render.js';
import { worstCaseSmsParams } from './sms-variables.js';
/**
 * Expéditeur d'un SMS : les deux seules formes que Brevo accepte.
 *
 * - **Alphanumérique**, 11 caractères maximum, sans espace ni ponctuation.
 * - **Numéro E.164**, que Brevo convertit en code court chez le destinataire.
 *
 * Ne vaut que pour un modèle **transactionnel**. En marketing, Brevo remplace
 * l'expéditeur par un code court de son pool — « the short code automatically
 * replaces the alphanumeric SenderID if you do not add an opt-out [STOP CODE] »
 * — et c'est précisément ce remplacement qui rend la réponse du client
 * possible. Deux envois de test l'ont confirmé, reçus de 36976 puis de 36199 :
 * ni le code, ni sa stabilité d'un envoi à l'autre ne nous appartiennent.
 *
 * Rien d'autre ne passe : un expéditeur libre accepté par l'API ferait du
 * back-office un relais d'usurpation, exactement comme pour le `from` des mails.
 */
export const smsSenderSchema = z
    .string()
    .trim()
    .min(1, 'Veuillez saisir un expéditeur.')
    .superRefine((value, ctx) => {
    if (/^[A-Za-z0-9]{1,11}$/.test(value))
        return;
    const normalized = normalizePhoneNumber(value);
    if (normalized && /^\+[1-9]\d{6,14}$/.test(normalized))
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expéditeur invalide : 11 caractères alphanumériques maximum (sans espace), ou un numéro de téléphone.',
    });
});
/** Destinataire d'un SMS : un numéro que `normalizePhoneNumber` sait ramener en E.164. */
export const smsRecipientSchema = z
    .string()
    .trim()
    .min(1, 'Veuillez saisir un destinataire.')
    .superRefine((value, ctx) => {
    const normalized = normalizePhoneNumber(value);
    if (normalized && /^\+[1-9]\d{6,14}$/.test(normalized))
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Numéro de téléphone invalide : « ${value} ».`,
    });
});
/**
 * Corps d'un modèle, validé sur son **pire cas** : variables substituées par
 * leur valeur la plus longue plausible, mention STOP réservée quand le modèle
 * est marketing.
 *
 * Deux refus distincts, parce qu'ils appellent deux corrections différentes :
 * un caractère hors GSM-7 se remplace (c'est presque toujours un collage depuis
 * un traitement de texte), un dépassement de 160 se réécrit.
 */
const smsTemplateBodySchema = (messageType) => z
    .string()
    .trim()
    .min(1, 'Veuillez saisir le texte du SMS.')
    .superRefine((body, ctx) => {
    const rendered = renderSms({
        body: normalizeSmsText(body),
        messageType,
        params: worstCaseSmsParams(body),
    });
    if (rendered.nonGsmChars.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Caractères non pris en charge par les SMS : ${rendered.nonGsmChars
                .map((char) => `« ${char} »`)
                .join(', ')}. Un seul suffit à diviser par deux la longueur autorisée.`,
        });
        return;
    }
    if (rendered.charCount > SMS_MAX_CHARS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: messageType === SmsMessageType.MARKETING
                ? `Ce SMS fait ${rendered.charCount} caractères (mention STOP comprise), pour ${SMS_MAX_CHARS} autorisés.`
                : `Ce SMS fait ${rendered.charCount} caractères, pour ${SMS_MAX_CHARS} autorisés.`,
        });
    }
});
/**
 * Le corps ne se valide qu'une fois la nature du message connue (la mention
 * STOP ne pèse qu'en marketing) : le contrôle est donc porté par l'objet, pas
 * par le champ. C'est le raffinement, écrit une fois et appliqué aux deux
 * schémas — un helper générique effaçait le type inféré en `any`, et le
 * `& { id: string }` des mutations partait avec.
 */
const refineBodyBudget = (value, ctx) => {
    const result = smsTemplateBodySchema(value.messageType).safeParse(value.body);
    if (result.success)
        return;
    for (const issue of result.error.issues) {
        ctx.addIssue({ ...issue, path: ['body'] });
    }
};
/**
 * Code d'un modèle : identifiant stable, cité par les envois automatiques et
 * figé sur chaque log. Majuscules et tirets bas seulement, pour qu'il reste
 * lisible dans un journal.
 */
export const smsTemplateCodeSchema = z
    .string()
    .trim()
    .min(2, 'Le code doit faire au moins 2 caractères.')
    .max(60, 'Le code ne doit pas dépasser 60 caractères.')
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Le code doit être en majuscules, sans espace (ex. « RESA_ATTENTE »).');
export const createSmsTemplateSchema = z
    .object({
    body: z.string(),
    code: smsTemplateCodeSchema,
    isActive: z.boolean().optional(),
    label: z.string().trim().min(1, 'Veuillez saisir un libellé.').max(120),
    messageType: z.nativeEnum(SmsMessageType),
    sender: smsSenderSchema,
})
    .superRefine(refineBodyBudget);
/**
 * La mise à jour reprend les mêmes champs : `body`, `messageType` et `sender`
 * sont interdépendants (le budget dépend de la nature, l'avertissement
 * « répondez » du couple corps/expéditeur), un PATCH partiel obligerait à
 * relire la ligne pour valider. Le formulaire renvoie de toute façon l'objet
 * complet. Seul `code` est absent : il est gelé après création.
 */
export const updateSmsTemplateSchema = z
    .object({
    body: z.string(),
    isActive: z.boolean(),
    label: z.string().trim().min(1, 'Veuillez saisir un libellé.').max(120),
    messageType: z.nativeEnum(SmsMessageType),
    sender: smsSenderSchema,
})
    .superRefine(refineBodyBudget);
/**
 * Corps commun à l'aperçu et à l'envoi d'un SMS de dossier : les deux routes
 * construisent le même message, seul l'envoi effectif les distingue.
 */
export const sendBookingSmsSchema = z.object({
    templateId: z.string().min(1),
    recipient: smsRecipientSchema,
    /** Surcharges saisies par l'agent dans l'aperçu, comme pour les mails. */
    templateParams: z.record(z.string()).optional(),
});
